<template>
    <div>
        <div class="d-flex align-center mb-1">
            <v-text-field
                v-model.trim="search_query"
                :label="$t('views.main.give_outs.search_label')"
                prepend-inner-icon="mdi-magnify"
                clearable
            ></v-text-field>

            <v-spacer></v-spacer>

            <span class="body-2 text--secondary d-none d-sm-inline">
                {{ from }}-{{ to }} {{ $t('views.main.give_outs.pagination_of') }} {{ total }}
            </span>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" :disabled="is_page_first" @click="prev_page">
                        <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('views.main.give_outs.pagination_prev_page') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" :disabled="is_page_last" @click="next_page">
                        <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('views.main.give_outs.pagination_next_page') }}</span>
            </v-tooltip>
        </div>

        <loading-message v-if="loading" :message="loading_message" class="my-5"></loading-message>

        <v-simple-table v-else-if="giveOuts.length">
            <template v-slot:default>
                <tbody>
                    <tr v-for="giveOut in giveOuts" :key="giveOut.GiveOutID">
                        <td class="text-no-wrap">
                            {{ giveOut.GiveOutName }}
                        </td>
                        <td>
                            {{ giveOut.Client.ClientName }}
                        </td>
                        <td>
                            <v-chip 
                                v-for="order in giveOut.OrdersClosed" 
                                :key="order.ClosedOrderID" 
                                x-small 
                                color="info" 
                                class="my-1 mr-1"
                            >
                                {{ order.OrderName }}
                            </v-chip>
                        </td>
                        <td>
                            {{ giveOut.GiveOutStatusName }}
                        </td>
                        <td class="text-right">
                            <v-menu
                                bottom
                                left
                                :offset-y="true"
                                rounded
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        small
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                    >
                                        <v-icon>mdi-dots-vertical</v-icon>
                                    </v-btn>
                                </template>

                                <v-list dense>
                                    <v-list-item>
                                        <v-list-item-title>
                                            <a @click="showGiveOutUpdateShipmentDialog(giveOut)">
                                                <v-icon small>mdi-truck</v-icon>
                                                {{ $t('views.main.give_outs.update_shipment') }}
                                            </a>
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </td>
                    </tr>
                </tbody>
            </template>
        </v-simple-table>

        <v-alert
            v-else
            border="left"
            colored-border
            type="info"
            elevation="1"
        >{{ empty_message }}</v-alert>

        <v-dialog
            v-model="GiveOutUpdateShipmentDialog"
            persistent
            width="500"
        >
            <v-card v-if="GiveOutToUpdateShipment">
                <v-card-title class="headline grey lighten-2 mb-5">
                    {{ $t('views.main.give_outs.shipment_details') }}
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        @click="hideGiveOutUpdateShipmentDialog"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                
                <v-card-text>
                    <v-text-field
                        v-model="giveOutShipmentForm.DespatchAdviceNumber"
                        :label="$t('views.main.give_outs.despatch_advice_number') + '*'"
                        :error="$store.getters.hasValidationError('DespatchAdviceNumber')"
                        :error-messages="$store.getters.validationError('DespatchAdviceNumber')"
                    ></v-text-field>
                    <v-menu
                        v-model="GiveOutUpdateShipmentDialogDespatchAdviceDatePicker"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="giveOutShipmentForm.DespatchAdviceDate"
                                :label="$t('views.main.give_outs.despatch_advice_date') + '*'"
                                prepend-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                                :error="$store.getters.hasValidationError('DespatchAdviceDate')"
                                :error-messages="$store.getters.validationError('DespatchAdviceDate')"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            v-model="giveOutShipmentForm.DespatchAdviceDate"
                            @input="GiveOutUpdateShipmentDialogDespatchAdviceDatePicker = false"
                        ></v-date-picker>
                    </v-menu>
                    <v-menu
                        v-model="GiveOutUpdateShipmentDialogEstimatedDeliveryDatePicker"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="giveOutShipmentForm.EstimatedDeliveryDate"
                                :label="$t('views.main.give_outs.estimated_delivery_date') + '*'"
                                prepend-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                                :error="$store.getters.hasValidationError('EstimatedDeliveryDate')"
                                :error-messages="$store.getters.validationError('EstimatedDeliveryDate')"
                            ></v-text-field>
                        </template>
                        <v-date-picker
                            v-model="giveOutShipmentForm.EstimatedDeliveryDate"
                            @input="GiveOutUpdateShipmentDialogEstimatedDeliveryDatePicker = false"
                        ></v-date-picker>
                    </v-menu>

                    <div class="my-4">
                        <div class="text-h6">
                            {{ $t('views.main.give_outs.package_line_numbers') }}
                        </div>
                        <div v-if="!giveOutShipmentForm.Packages.length" class="error--text">
                            {{ $t('views.main.give_outs.no_packages') }}
                        </div>
                        <template v-else>
                            <v-text-field
                                v-for="(p, i) in giveOutShipmentForm.Packages"
                                :key="p.PackageID"
                                v-model="p.IDx"
                                :label="'EAN: ' + giveOutPackageEANs[p.PackageID] + '*'"
                                :error="$store.getters.hasValidationError(`Packages.${i}.IDx`)"
                                :error-messages="$store.getters.validationError(`Packages.${i}.IDx`)"
                            ></v-text-field>
                        </template>
                    </div>

                    <small>* {{ $t('views.main.give_outs.field_required') }}</small>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="success"
                        rounded
                        @click="updateGiveOutShipment"
                        :loading="GiveOutShipmentUpdating"
                    >
                        {{ $t('views.main.give_outs.save_button') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import LoadingMessage from "../../../components/LoadingMessage";

export default {
    name: "GiveOuts",

    components: {
        LoadingMessage,
    },

    data() {
        return {
            search_query: "",
            loading: false,

            GiveOutUpdateShipmentDialog: false,
            GiveOutUpdateShipmentDialogDespatchAdviceDatePicker: false,
            GiveOutUpdateShipmentDialogEstimatedDeliveryDatePicker: false,
            GiveOutToUpdateShipment: null,
            GiveOutShipmentUpdating: false,

            giveOuts: [],

            giveOutShipmentForm: {
                DespatchAdviceNumber: null,
                DespatchAdviceDate: null,
                EstimatedDeliveryDate: null,
                Packages: [],
            },

            giveOutPackageEANs: [],


            // pagination
            page: 1,
            last_page:1,
            from: 0,
            to: 0,
            total: 0,
        }
    },

    computed: {
        is_mobile() {
            return this.$vuetify.breakpoint.mobile;
        },

        is_page_first() {
            return this.page === 1;
        },

        is_page_last() {
            return this.page === this.last_page;
        },

        loading_message() {
            return !this.search_query
                ? this.$t('views.main.give_outs.loading_give_outs')
                : this.$t('views.main.give_outs.searching_give_outs');
        },

        empty_message() {
            return !this.search_query
                ? this.$t('views.main.give_outs.no_give_outs')
                : this.$t('views.main.give_outs.no_give_outs_matching_query');
        },

        locale: function () {
            return this.$i18n.locale;
        },
    },

    methods: {
        next_page: function () {
            if(this.page < this.last_page) this.page++;
        },
        prev_page: function () {
            if(this.page > 1) this.page--;
        },

        fetch: function () {
            this.loading = true;

            this.$store.dispatch('loadGiveOuts', {
                page: this.page,
                search_query: this.search_query
            }).then(response => {
                if(response.status === 200) {
                    this.giveOuts = response.data.data;
                    this.page = response.data.meta.current_page ?? 1;
                    this.last_page = response.data.meta.last_page ?? 1;
                    this.from = response.data.meta.from ?? 0;
                    this.to = response.data.meta.to ?? 0;
                    this.total = response.data.meta.total;
                } else {
                    this.$store.commit(
                        "showSnackbarError",
                        this.$t('views.main.give_outs.loading_give_outs_failed')
                    );
                }
            })
                .catch(() => {})
                .then(() => {
                    this.loading = false;
                });
        },

        updateGiveOutShipment: function () {
            this.GiveOutShipmentUpdating = true;

            this.$store.dispatch('updateGiveOutShipment', {
                GiveOutID: this.GiveOutToUpdateShipment.GiveOutID,
                formData: this.giveOutShipmentForm,
                method: !this.GiveOutToUpdateShipment.Shipment ? 'post' : 'put'
            }).then(response => {
                if([200, 201].includes(response.status)) {
                    this.hideGiveOutUpdateShipmentDialog();
                    this.fetch();
                    this.$store.commit(
                        "showSnackbarSuccess",
                        this.$t('views.main.give_outs.give_out_shipment_updated')
                    );
                } else {
                    this.$store.commit(
                        "showSnackbarError",
                        this.$t('views.main.give_outs.give_out_shipment_updating_failed')
                    );
                }
            })
                .catch(() => {})
                .then(() => {
                    this.GiveOutShipmentUpdating = false;
                });
        },

        showGiveOutUpdateShipmentDialog: function (GiveOut) {
            this.GiveOutToUpdateShipment = GiveOut;
        },
        hideGiveOutUpdateShipmentDialog: function () {
            this.GiveOutUpdateShipmentDialog = false;
        },
        resetGiveOutShipmentForm: function () {
            this.giveOutShipmentForm.DespatchAdviceNumber = null;
            this.giveOutShipmentForm.DespatchAdviceDate = null;
            this.giveOutShipmentForm.EstimatedDeliveryDate = null;
            this.giveOutShipmentForm.Packages = [];
        },
        setGiveOutShipmentForm: function (GiveOut) {
            this.giveOutPackageEANs = GiveOut.OrdersClosed
                .reduce((a1, c1) => a1.concat(c1.Packages), [])
                .reduce((a2, c2) => ({...a2, [c2.PackageID]: c2.EAN }), {})
                
            if (!GiveOut.Shipment) {
                this.resetGiveOutShipmentForm();
                this.giveOutShipmentForm.Packages = [...new Set(GiveOut.OrdersClosed
                    .reduce((a, c) => a.concat(c.Packages), [])
                    .map(p => p.PackageID)
                )].map((PackageID, index) => ({ PackageID, IDx: index + 1 }));
            } else {
                this.giveOutShipmentForm.DespatchAdviceNumber = GiveOut.Shipment.DespatchAdviceNumber;
                this.giveOutShipmentForm.DespatchAdviceDate = GiveOut.Shipment.DespatchAdviceDate;
                this.giveOutShipmentForm.EstimatedDeliveryDate = GiveOut.Shipment.EstimatedDeliveryDate;
                this.giveOutShipmentForm.Packages = GiveOut.Shipment.ShipmentPackages.map(
                    p => ({ PackageID: p.PackageID, IDx: p.IDx })
                );
            }
        },
    },

    created() {
        this.debouncedFetch = _.debounce(this.fetch, 900);
        this.debouncedFetchFast = _.debounce(this.fetch, 300);

        this.fetch();
    },

    watch: {
        search_query: function () {
            //If any request_search is currently in progress, cancel it!
            if(this.$store.state.sources.load_give_outs) {
                this.$store.state.sources.load_give_outs.cancel();
            }

            //Run debouncedFetch
            this.debouncedFetch();
        },
        page: function () {
            //If any request_search is currently in progress, cancel it!
            if(this.$store.state.sources.load_give_outs) {
                this.$store.state.sources.load_give_outs.cancel();
            }

            //Run debouncedFetch
            this.debouncedFetchFast();
        },
        GiveOutToUpdateShipment: function (new_value) {
            if(new_value !== null) {
                this.setGiveOutShipmentForm(new_value);
                this.GiveOutUpdateShipmentDialog = true;
            }
        },
        GiveOutUpdateShipmentDialog: function (new_value) {
            if(!new_value) {
                this.resetGiveOutShipmentForm();
                this.$store.commit("resetValidation");
                this.GiveOutToUpdateShipment = null;
            }
        },
        locale() {
            this.fetch();
        },
    }
}
</script>
